// TeamCustomer.jsx
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography
} from "@mui/material";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase-key";
import { useAuth } from "../state/appContext";


const documentSections = {
  customer_kyc: [
    "PHOTO",
    "PAN",
    "AADHAR",
    "LAND DOCUMENTS",
    "RENT / LEASE DOCUMENTS",
    "MORTGAGE DOCUMENTS",
    "ELECTRICITY BILL",
    "JATI/MINORITY CERTIFICATE",
    "MOOL NIWAS",
    "LIC RECEIPT",
    "STAMP OF FIRM",
    "MARKSHEET",
  ],
  business_kyc: [
    "UDYAM REGISTRATION",
    "BRN REGISTRATION",
    "GST REGISTRATION",
    "FSSAI REGISTRATION",
    "MSME REGISTRATION",
    "GOOGLE LOCATION",
    "LAND PHOTO",
    "LAND 360 VIDEO",
    "1ST SALE BILL",
    "GST DEPOSIT RECEIPT",
    "LETTERHEAD SEAL SIGN",
    "MANDI LICENSE",
  ],
  banking: [
    "INCOME TAX RETURN - 3 YEARS",
    "BANK STATEMENT - 1 YEAR",
    "PROJECT REPORT",
    "CMA DATA",
    "PLANT & MACH QUOTATION",
    "BUILDING QUOTATION",
    "CANCELLED CHEQUE",
    "GST RETURN (1 YEAR)",
    "AUDIT REPORT",
  ],
  bank_related: [
    "APPLICATION FORM",
    "ASSETS & LIABILITIES",
    "PROPERTY LEGAL REPORT",
    "VALUATION REPORT",
    "FI REPORT",
    "SANCTION LETTER",
  ],
};

const TeamCustomer = () => {
  const navigate = useNavigate();
  const { customerId } = useParams(); // URL -> team/:customerId
  const { user, userType, loading } = useAuth();

  const [customer, setCustomer] = useState(null);
  const [docs, setDocs] = useState({});
  const [loadingPage, setLoadingPage] = useState(true);
  const [uploading, setUploading] = useState(false);

  // -------------------------------
  // SECURITY CHECKS
  // -------------------------------
  const verifyAccess = useCallback(async () => {
    if (loading) return;

    if (!user || userType !== "team") {
      toast.error("Access denied");
      navigate("/login");
      return;
    }

    // Fetch customer doc
    const snap = await getDoc(doc(db, "users", customerId));
    if (!snap.exists()) {
      toast.error("Customer not found");
      // navigate(-1);
      return;
    }

    const customerData = snap.data();

    // Check if this team member is actually assigned
    if (!customerData.employeeId.includes(user.uid)) {
      toast.error("Not assigned to this customer");
      // navigate(-1);
      return;
    }

    setCustomer({ id: customerId, ...customerData });
  }, [customerId, loading, navigate, user, userType]);

  // -------------------------------
  // LOAD DOCUMENTS
  // -------------------------------
  const loadDocuments = useCallback(async () => {
    if (!customerId) return;

    const path = collection(db, "users", customerId, "documents");
    const snap = await getDocs(path);

    const out = {};
    snap.forEach((d) => {
      out[d.id] = d.data();
    });

    setDocs(out);
  }, [customerId]);

  useEffect(() => {
    (async () => {
      await verifyAccess();
      await loadDocuments();
      setLoadingPage(false);
    })();
  }, [verifyAccess, loadDocuments]);

  // -------------------------------
  // UPLOAD FILE
  // -------------------------------
  const uploadDocument = async (section, name, file) => {
    if (!file) return;
    setUploading(true);

    try {
      const path = `user_documents/${customerId}/${section}/${name}.pdf`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await setDoc(
        doc(db, "users", customerId, "documents", `${section}__${name}`),
        {
          name,
          section,
          url,
          updated_at: new Date(),
        },
        { merge: true }
      );

      toast.success(`${name} uploaded`);
      loadDocuments();
    } catch (err) {
      toast.error("Upload failed");
      console.error(err);
    }

    setUploading(false);
  };

  if (loading || loadingPage) {
    return (
      <Box className="min-h-screen flex items-center justify-center text-lg">
        Loading...
      </Box>
    );
  }

  return (
    <Box className="py-28 px-6 min-h-screen">
      <ToastContainer />
      <Button onClick={() => navigate(-1)} sx={{ mb: 3 }}>
        ← Back to Team Dashboard
      </Button>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        Customer: {customer?.customer_name || customer?.email}
      </Typography>

      <Divider sx={{ mb: 4 }} />

      {/* --------------------------
          CUSTOMER DETAILS
      --------------------------- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Customer Information
        </Typography>

        <Grid container spacing={2}>
          {customer && (
            <Grid container spacing={2}>
              {Object.keys(customer)
                .filter(
                  (k) =>
                    !["documents", "employeeId", "password", "created_at"].includes(k)
                )
                .map((key) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {key.replace(/_/g, " ").toUpperCase()}
                    </Typography>
                    <Typography variant="body1">
                      {String(customer[key] ?? "-")}
                    </Typography>
                  </Grid>
                ))}
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* --------------------------
          DOCUMENT SECTIONS
      --------------------------- */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Documents (Upload / View / Download)
        </Typography>

        {Object.entries(documentSections).map(([section, items]) => (
          <Box key={section} sx={{ mb: 5 }}>
            <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
              {section.replace("_", " ")}
            </Typography>

            <Grid container spacing={3}>
              {items.map((docName) => {
                const key = `${section}__${docName}`;
                const existing = docs[key];

                return (
                  <Grid item xs={12} sm={6} md={4} key={docName}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography fontWeight={600}>{docName}</Typography>

                      {/* Upload */}
                      <input
                        type="file"
                        accept="application/pdf"
                        disabled={uploading}
                        onChange={(e) =>
                          uploadDocument(section, docName, e.target.files[0])
                        }
                        className="mt-2 text-sm"
                      />

                      {/* Download link */}
                      {existing?.url && (
                        <a
                          href={existing.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline text-sm mt-2 block"
                        >
                          View / Download
                        </a>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default TeamCustomer;
